/**
 * Shapes of the role routes and the keys a role may not take (SC-439, phase D).
 *
 * Types, the reserved key list and the shape of a custom key, nothing else: cloud has no copy of
 * this file (it does not import `types/`; `cloud/utils/roleKeys.js` holds the same list and
 * pattern), and `rbacCloudParity.test.ts` compares only `catalogue.ts` and `evaluate.ts`. The
 * server builds these shapes in `server/services/rbacManagement.ts`; the client and the coming
 * admin panel read them.
 *
 * A role is attached to people, defaults, invite links and pending adds by its `key`, which never
 * changes after the role is created. The `name` is for display and a platform admin may rename any
 * role at any time, so nothing may look a role up by name.
 */

import type { PermissionKey } from './catalogue'

/**
 * Keys no custom role may take: the system roles and the legacy role strings old app bundles still
 * send (`user`, `moderator`, `manager`), which the servers map to a system role. A custom role
 * with one of these keys would be read as the system role it shadows.
 */
export const RESERVED_ROLE_KEYS = [
  'owner',
  'admin',
  'member',
  'user',
  'moderator',
  'manager',
] as const

/** What a custom role's key may look like. */
export const ROLE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/

/** Whether `value` is a key a custom role may have (the role may or may not exist). */
export function isCustomRoleKey(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    ROLE_KEY_PATTERN.test(value) &&
    !(RESERVED_ROLE_KEYS as readonly string[]).includes(value)
  )
}

/** A role as a company's people see it (`GET /api/companies/:companyId/roles`). */
export interface RoleView {
  key: string
  name: string
  description: string
  isSystem: boolean
  scope: 'global' | 'company'
  permissions: PermissionKey[]
}

/** A role as the platform admin API returns it. `companyId` is null for a global role. */
export interface AdminRoleView extends RoleView {
  id: string
  companyId: string | null
  updatedBy: string | null
}

/**
 * What still points at a role by its key. A role with any count above zero, or that is the
 * platform default, cannot be deleted (409 `role_in_use`, carrying this object).
 */
export interface RoleUsage {
  /** Membership rows with `roleKey` = the key, active or not (in the role's company, if any). */
  members: number
  /** Companies whose `settings.default_role_key` is the key. */
  companiesDefault: number
  /** Active workspace invite links with `roleKey` = the key. */
  inviteLinks: number
  /** Pending paid adds (`PendingUserAddition` with status `pending`) with `roleKey` = the key. */
  pendingAdds: number
  /** Whether the role is the platform default role. Only a global role can be. */
  platformDefault: boolean
}

/** `GET /api/admin/rbac/roles/:roleId`. */
export interface AdminRoleDetail {
  role: AdminRoleView
  usage: RoleUsage
}

/** `PUT /api/admin/rbac/roles/:roleId/permissions`: the role, plus what the edit did to invite links. */
export interface RolePermissionsUpdateView {
  role: AdminRoleView
  /**
   * Active workspace invite links whose token was replaced (old link off, new one made) because
   * this edit added a key to the role, so earlier copies of a link that gives the role stop
   * working. 0 when no key was added (removing keys, or the same set, replaces nothing).
   */
  linksRotated: number
}

/** `GET /api/companies/:companyId/roles`. */
export interface CompanyRolesView {
  roles: RoleView[]
  /**
   * The role a person of this company is given when they join with none, or whose own is gone:
   * the company default if it can be used, else the platform default, else `member`.
   */
  defaultRoleKey: string
}

/** `GET` and `PUT /api/admin/rbac/platform-default-role`. */
export interface PlatformDefaultRoleView {
  /** What is stored (a global role key, never `owner`), or null when none is set. */
  roleKey: string | null
  /** What is applied: `roleKey` while that role exists and is active, else `member`. */
  effectiveRoleKey: string
}

/** The `PUT` answer: the view, plus what the change did to workspace invite links. */
export interface PlatformDefaultRoleUpdateView extends PlatformDefaultRoleView {
  /**
   * Active invite links whose token was replaced (old link off, new one made) because this change
   * gave the platform default a permission the old one lacked, so earlier copies of a link that
   * follows the default stop working. 0 for any other change.
   */
  linksRotated: number
}
